import React, { memo, useEffect, useMemo, useState, useRef, forwardRef, useImperativeHandle, useCallback } from "react";
import * as XLSX from "xlsx";
import "./SimpleExcelViewer.css";

const MIN_VISIBLE_ROWS = 200;
const MIN_VISIBLE_COLS = 26;
const EXTRA_SCROLL_ROWS = 60;
const EXTRA_SCROLL_COLS = 8;
const DEFAULT_COL_WIDTH = 88;
const DEFAULT_ROW_HEIGHT = 24;
const MIN_COL_WIDTH = 48;
const MIN_ROW_HEIGHT = 20;

function escapeHtml(value) {
  const s = value == null ? "" : String(value);
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function textToHtml(value) {
  return escapeHtml(value).replace(/\r\n|\r|\n/g, "<br/>");
}

function richTextToHtml(richTextRuns, themeColors) {
  if (!Array.isArray(richTextRuns) || richTextRuns.length === 0) return "";
  return richTextRuns.map((run) => {
    const t = run?.text ?? "";
    const f = run?.font || {};
    const style = {};
    const fontColor = resolveExcelColorToCss(f.color, themeColors);
    if (fontColor) style.color = fontColor;
    if (f.bold) style.fontWeight = 700;
    if (f.italic) style.fontStyle = "italic";
    if (f.underline) style.textDecoration = "underline";
    if (typeof f.name === "string") style.fontFamily = f.name;
    if (typeof f.size === "number") style.fontSize = `${Math.max(8, Math.round(f.size))}px`;
    const styleAttr = Object.keys(style).length
      ? ` style="${Object.entries(style).map(([k, v]) => `${k}:${String(v)}`).join(";")}"`
      : "";
    return `<span${styleAttr}>${textToHtml(t)}</span>`;
  }).join("");
}

function rgbToCss(rgb) {
  if (!rgb || typeof rgb !== "string") return undefined;
  const hex = rgb.length === 8 ? rgb.slice(2) : rgb;
  if (!/^[0-9a-fA-F]{6}$/.test(hex)) return undefined;
  return `#${hex}`;
}

function argbToCss(argb) {
  if (!argb || typeof argb !== "string") return undefined;
  const hex = argb.length === 8 ? argb.slice(2) : argb;
  if (!/^[0-9a-fA-F]{6}$/.test(hex)) return undefined;
  return `#${hex}`;
}

function clampByte(n) {
  return Math.max(0, Math.min(255, n));
}

function applyTintToHex(hex, tint) {
  if (typeof tint !== "number" || Number.isNaN(tint) || tint === 0) return hex;
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);

  const t = Math.max(-1, Math.min(1, tint));
  const blend = (c) => {
    if (t < 0) return clampByte(Math.round(c * (1 + t)));
    return clampByte(Math.round(c + (255 - c) * t));
  };

  const rr = blend(r).toString(16).padStart(2, "0");
  const gg = blend(g).toString(16).padStart(2, "0");
  const bb = blend(b).toString(16).padStart(2, "0");
  return `${rr}${gg}${bb}`;
}

// Standard Excel indexed palette (first 64). Many files use this for older styles.
const EXCEL_INDEXED_COLORS = [
  "000000", "FFFFFF", "FF0000", "00FF00", "0000FF", "FFFF00", "FF00FF", "00FFFF",
  "000000", "FFFFFF", "FF0000", "00FF00", "0000FF", "FFFF00", "FF00FF", "00FFFF",
  "800000", "008000", "000080", "808000", "800080", "008080", "C0C0C0", "808080",
  "9999FF", "993366", "FFFFCC", "CCFFFF", "660066", "FF8080", "0066CC", "CCCCFF",
  "000080", "FF00FF", "FFFF00", "00FFFF", "800080", "800000", "008080", "0000FF",
  "00CCFF", "CCFFFF", "CCFFCC", "FFFF99", "99CCFF", "FF99CC", "CC99FF", "FFCC99",
  "3366FF", "33CCCC", "99CC00", "FFCC00", "FF9900", "FF6600", "666699", "969696",
  "003366", "339966", "003300", "333300", "993300", "993366", "333399", "333333",
];

function resolveExcelColorToCss(color, themeColors) {
  if (!color || typeof color !== "object") return undefined;

  // exceljs sometimes provides argb directly
  if (color.argb) return argbToCss(String(color.argb));

  // theme-based color
  if (typeof color.theme === "number" && Array.isArray(themeColors)) {
    const base = themeColors[color.theme];
    if (base) {
      const tinted = applyTintToHex(base, typeof color.tint === "number" ? color.tint : 0);
      return `#${tinted}`;
    }
  }

  // indexed palette
  if (typeof color.indexed === "number") {
    const base = EXCEL_INDEXED_COLORS[color.indexed];
    if (base) return `#${base}`;
  }

  // rgb (rare)
  if (color.rgb) return rgbToCss(String(color.rgb));

  return undefined;
}

function toColumnLabel(index) {
  let value = index + 1;
  let label = "";
  while (value > 0) {
    const mod = (value - 1) % 26;
    label = String.fromCharCode(65 + mod) + label;
    value = Math.floor((value - 1) / 26);
  }
  return label;
}

function getColumnWidthPx(colMeta) {
  if (!colMeta) return DEFAULT_COL_WIDTH;
  if (typeof colMeta.wpx === "number") return Math.max(MIN_COL_WIDTH, Math.round(colMeta.wpx));
  if (typeof colMeta.wch === "number") return Math.max(MIN_COL_WIDTH, Math.round(colMeta.wch * 8 + 12));
  return DEFAULT_COL_WIDTH;
}

function getRowHeightPx(rowMeta) {
  if (!rowMeta) return DEFAULT_ROW_HEIGHT;
  if (typeof rowMeta.hpx === "number") return Math.max(MIN_ROW_HEIGHT, Math.round(rowMeta.hpx));
  if (typeof rowMeta.hpt === "number") return Math.max(MIN_ROW_HEIGHT, Math.round(rowMeta.hpt * (96 / 72)));
  return DEFAULT_ROW_HEIGHT;
}

function buildSheetModel(ws) {
  const ref = ws?.["!ref"] || "A1";
  const range = XLSX.utils.decode_range(ref);
  const dataRowsCount = Math.max(range.e.r + 1, 1);
  const dataColsCount = Math.max(range.e.c + 1, 1);
  const targetCols = Math.max(dataColsCount + EXTRA_SCROLL_COLS, MIN_VISIBLE_COLS);
  const targetRows = Math.max(dataRowsCount + EXTRA_SCROLL_ROWS, MIN_VISIBLE_ROWS);

  const rows = new Array(targetRows).fill(null).map(() => new Array(targetCols).fill(""));
  const formulas = {};
  const styles = {};

  for (let r = 0; r <= range.e.r; r += 1) {
    for (let c = 0; c <= range.e.c; c += 1) {
      const addr = XLSX.utils.encode_cell({ r, c });
      const cell = ws[addr];
      if (!cell) continue;

      rows[r][c] = cell.w ?? cell.v ?? "";
      if (cell.f) formulas[`${r}:${c}`] = `=${cell.f}`;

      const s = cell.s;
      if (s) {
        const css = {};
        const fontColor = rgbToCss(s.font?.color?.rgb);
        const fillColor = rgbToCss(s.fill?.fgColor?.rgb);
        if (fontColor) css.color = fontColor;
        if (fillColor) css.backgroundColor = fillColor;
        if (s.font?.bold) css.fontWeight = 700;
        if (s.font?.italic) css.fontStyle = "italic";
        if (s.font?.underline) css.textDecoration = "underline";
        if (typeof s.font?.name === "string") css.fontFamily = s.font.name;
        if (typeof s.font?.sz === "number") css.fontSize = `${Math.max(8, Math.round(s.font.sz))}px`;
        if (Object.keys(css).length > 0) styles[`${r}:${c}`] = css;
      }
    }
  }

  const rawCols = Array.isArray(ws?.["!cols"]) ? ws["!cols"] : [];
  const rawRows = Array.isArray(ws?.["!rows"]) ? ws["!rows"] : [];
  const colWidths = Array.from({ length: targetCols }, (_, i) => getColumnWidthPx(rawCols[i]));
  const rowHeights = Array.from({ length: targetRows }, (_, i) => getRowHeightPx(rawRows[i]));

  return { rows, maxCols: targetCols, colWidths, rowHeights, formulas, styles };
}

// Helper: find elements by local name (ignoring XML namespace prefixes like "c:", "xdr:", etc.)
// Uses getElementsByTagNameNS with wildcard namespace for reliable namespace-agnostic lookups.
function qLocal(parent, localName) {
  if (!parent) return null;
  // getElementsByTagNameNS('*', name) matches elements with the given localName in ANY namespace
  const results = parent.getElementsByTagNameNS('*', localName);
  return results.length > 0 ? results[0] : null;
}

function qLocalAll(parent, localName) {
  if (!parent) return [];
  return Array.from(parent.getElementsByTagNameNS('*', localName));
}

// Helper: find DIRECT child elements by local name only (not deep descendants)
function qDirectChild(parent, localName) {
  if (!parent) return null;
  for (let i = 0; i < parent.childNodes.length; i++) {
    const child = parent.childNodes[i];
    if (child.nodeType === 1 && child.localName === localName) return child;
  }
  return null;
}

async function extractChartsFromZip(zip) {
  const chartsBySheet = {};
  try {
    const wbXml = await zip.file("xl/workbook.xml")?.async("string");
    if (!wbXml) return chartsBySheet;
    const wbDoc = new DOMParser().parseFromString(wbXml, "application/xml");
    const sheetNodes = qLocalAll(wbDoc, "sheet");
    const sheetIdToName = {};
    sheetNodes.forEach(node => { sheetIdToName[node.getAttribute("r:id")] = node.getAttribute("name"); });

    const wbRelsXml = await zip.file("xl/_rels/workbook.xml.rels")?.async("string");
    if (!wbRelsXml) return chartsBySheet;
    const wbRelsDoc = new DOMParser().parseFromString(wbRelsXml, "application/xml");
    const sheetPathToName = {};
    qLocalAll(wbRelsDoc, "Relationship").forEach(rel => {
      const target = rel.getAttribute("Target");
      const id = rel.getAttribute("Id");
      if (sheetIdToName[id]) sheetPathToName[target] = sheetIdToName[id];
    });

    for (const sheetPath of Object.keys(sheetPathToName)) {
      const sheetName = sheetPathToName[sheetPath];
      const parts = sheetPath.split("/");
      const filename = parts.pop();
      const sheetRelsPath = `xl/${parts.join("/")}/_rels/${filename}.rels`.replace(/\/\//g, "/");

      const sheetRelsFile = zip.file(sheetRelsPath);
      if (!sheetRelsFile) continue;

      const relsXml = await sheetRelsFile.async("string");
      const relsDoc = new DOMParser().parseFromString(relsXml, "application/xml");

      const drawingPaths = [];
      qLocalAll(relsDoc, "Relationship").forEach(rel => {
        if (rel.getAttribute("Type").includes("/drawing")) {
          let target = rel.getAttribute("Target");
          if (target.startsWith("../")) target = target.substring(3);
          drawingPaths.push(`xl/${target}`);
        }
      });

      if (!chartsBySheet[sheetName]) chartsBySheet[sheetName] = [];
      for (const drawingPath of drawingPaths) {
        const drawingFile = zip.file(drawingPath);
        if (!drawingFile) continue;

        const partsD = drawingPath.split("/");
        const filenameD = partsD.pop();
        const drawingRelsPath = `${partsD.join("/")}/_rels/${filenameD}.rels`;

        const drawingRelsFile = zip.file(drawingRelsPath);
        const drawingRelsMap = {};
        if (drawingRelsFile) {
          const dRelsXml = await drawingRelsFile.async("string");
          const dRelsDoc = new DOMParser().parseFromString(dRelsXml, "application/xml");
          qLocalAll(dRelsDoc, "Relationship").forEach(rel => {
            let target = rel.getAttribute("Target");
            if (target.startsWith("../")) target = target.substring(3);
            drawingRelsMap[rel.getAttribute("Id")] = `xl/${target}`;
          });
        }

        const drawingXml = await drawingFile.async("string");
        const drawingDoc = new DOMParser().parseFromString(drawingXml, "application/xml");

        qLocalAll(drawingDoc, "twoCellAnchor").forEach((anchor, idx) => {
          const fromNode = qLocal(anchor, "from");
          const toNode = qLocal(anchor, "to");
          const fromCol = parseInt(qLocal(fromNode, "col")?.textContent || "0");
          const fromRow = parseInt(qLocal(fromNode, "row")?.textContent || "0");
          const fromColOff = parseInt(qLocal(fromNode, "colOff")?.textContent || "0");
          const fromRowOff = parseInt(qLocal(fromNode, "rowOff")?.textContent || "0");
          const toCol = parseInt(qLocal(toNode, "col")?.textContent || "0");
          const toRow = parseInt(qLocal(toNode, "row")?.textContent || "0");
          const toColOff = parseInt(qLocal(toNode, "colOff")?.textContent || "0");
          const toRowOff = parseInt(qLocal(toNode, "rowOff")?.textContent || "0");

          // Look for chart reference inside graphicFrame > graphic > graphicData > chart
          const graphicFrame = qLocal(anchor, "graphicFrame");
          const graphicData = qLocal(graphicFrame || anchor, "graphicData");
          const chartNode = qLocal(graphicData || anchor, "chart");
          if (!chartNode) return;

          const rId = chartNode.getAttribute("r:id");
          const chartPath = drawingRelsMap[rId];
          if (!chartPath) return;

          chartsBySheet[sheetName].push({
            id: `chart-${sheetName}-${idx}`,
            type: 'chart',
            chartPath,
            range: {
              tl: { col: fromCol, row: fromRow, colOff: fromColOff, rowOff: fromRowOff },
              br: { col: toCol, row: toRow, colOff: toColOff, rowOff: toRowOff }
            }
          });
        });
      }
    }

    // Build chart Data parsing (using namespace-aware local name lookups)
    for (const sheetName in chartsBySheet) {
      for (const chartObj of chartsBySheet[sheetName]) {
        const chartFile = zip.file(chartObj.chartPath);
        if (!chartFile) {
          // Also try without leading "xl/" or with different path resolution
          const altPath = chartObj.chartPath.replace(/^xl\//, '');
          const altFile = zip.file(`xl/${altPath}`) || zip.file(altPath);
          if (!altFile) { chartObj.placeholder = true; continue; }
        }
        const actualFile = zip.file(chartObj.chartPath) || zip.file(chartObj.chartPath.replace(/^xl\//, ''));
        if (!actualFile) { chartObj.placeholder = true; continue; }

        const chartXml = await actualFile.async("string");
        const chartDoc = new DOMParser().parseFromString(chartXml, "application/xml");

        // Parse title: c:chartSpace > c:chart > c:title > c:tx > c:rich > a:p > a:r > a:t
        // Scope to c:chart element first so we don't accidentally grab axis titles etc.
        let chartTitle = "";
        const chartElem = qLocal(chartDoc, "chart");
        const titleNode = chartElem ? qDirectChild(chartElem, "title") : qLocal(chartDoc, "title");
        if (titleNode) {
          // Try rich text path: c:tx > c:rich > a:p > a:r > a:t
          const txNode = qLocal(titleNode, "tx");
          if (txNode) {
            // Rich text path
            const richNode = qLocal(txNode, "rich");
            if (richNode) {
              const tNodes = qLocalAll(richNode, "t");
              if (tNodes.length > 0) {
                chartTitle = tNodes.map(t => t.textContent).join("").trim();
              }
            }
            // strRef > strCache > pt > v path
            if (!chartTitle) {
              const strCache = qLocal(txNode, "strCache");
              if (strCache) {
                const vNode = qLocal(strCache, "v");
                if (vNode) chartTitle = vNode.textContent.trim();
              }
            }
          }
          // Fallback: search any text elements
          if (!chartTitle) {
            const fallbackTexts = qLocalAll(titleNode, "t");
            if (fallbackTexts.length > 0) {
              chartTitle = fallbackTexts.map(t => t.textContent).join("").trim();
            }
          }
        }
        chartObj.title = chartTitle || "Chart";
        console.log('[Chart] Title parsed:', JSON.stringify(chartTitle), 'from titleNode:', !!titleNode);

        // Find plotArea
        const plotArea = qLocal(chartDoc, "plotArea");
        if (!plotArea) { chartObj.placeholder = true; continue; }

        // Scan ALL chart type elements in plotArea to support combo charts
        // (e.g., barChart + lineChart in the same plotArea)
        const chartTypeNames = ["barChart", "bar3DChart", "lineChart", "line3DChart",
          "pieChart", "pie3DChart", "doughnutChart", "areaChart", "area3DChart",
          "scatterChart", "radarChart", "bubbleChart", "stockChart", "surfaceChart"];

        const chartGroups = []; // { element, typeName, renderType }
        for (const typeName of chartTypeNames) {
          const found = qLocalAll(plotArea, typeName);
          found.forEach(el => {
            // Determine render type for series in this group
            let renderType = typeName.replace('3DChart', '').replace('Chart', '');
            chartGroups.push({ element: el, typeName, renderType });
          });
        }

        if (chartGroups.length === 0) { chartObj.placeholder = true; continue; }

        // Primary chart type is the first group found
        chartObj.chartType = chartGroups[0].renderType;
        // Mark as combo if multiple different render types exist
        const uniqueTypes = [...new Set(chartGroups.map(g => g.renderType))];
        chartObj.isCombo = uniqueTypes.length > 1;

        chartObj.series = [];
        let allSerNodes = [];

        // Parse series from ALL chart groups
        for (const group of chartGroups) {
          const serNodes = qLocalAll(group.element, "ser");
          serNodes.forEach(ser => {
            // Series name: c:tx > c:strRef > c:strCache > c:pt > c:v
            let seriesName = "";
            const txNode = qLocal(ser, "tx");
            if (txNode) {
              // Path 1: strRef > strCache > pt > v (most common for named series)
              const strRef = qLocal(txNode, "strRef");
              if (strRef) {
                const strCache = qLocal(strRef, "strCache");
                if (strCache) {
                  const vNode = qLocal(strCache, "v");
                  if (vNode) seriesName = vNode.textContent.trim();
                }
                // If strCache not populated, try to extract from formula reference
                if (!seriesName) {
                  const fNode = qLocal(strRef, "f");
                  if (fNode) {
                    const fText = fNode.textContent.trim();
                    // Extract column header from formula like "Sheet1!$B$1"
                    // We can't resolve it but we can show the reference
                    seriesName = fText.replace(/^.*!/, '').replace(/\$/g, '');
                  }
                }
              }
              // Path 2: direct v node (simple string value)
              if (!seriesName) {
                const directV = qDirectChild(txNode, "v");
                if (directV) seriesName = directV.textContent.trim();
              }
              // Path 3: rich text (a:r > a:t)
              if (!seriesName) {
                const richNode = qLocal(txNode, "rich");
                if (richNode) {
                  const txTexts = qLocalAll(richNode, "t");
                  if (txTexts.length > 0) {
                    seriesName = txTexts.map(t => t.textContent).join("").trim();
                  }
                }
              }
            }
            // Fallback: use order index
            if (!seriesName) {
              const orderNode = qLocal(ser, "order");
              const orderVal = orderNode ? orderNode.getAttribute("val") : null;
              seriesName = `Series${orderVal != null ? parseInt(orderVal) + 1 : ''}`;
            }

            // Values from c:val > c:numRef > c:numCache > c:pt > c:v
            const valNode = qLocal(ser, "val") || qLocal(ser, "yVal");
            const numCache = qLocal(valNode, "numCache") || valNode;
            const ptNodes = numCache ? qLocalAll(numCache, "pt") : [];
            const vals = ptNodes.map(pt => {
              const v = qLocal(pt, "v");
              return v ? parseFloat(v.textContent) : 0;
            }).filter(v => !isNaN(v));

            if (vals.length > 0) {
              chartObj.series.push({
                name: seriesName,
                data: vals,
                renderType: group.renderType, // 'bar', 'line', 'pie', etc.
              });
            }
          });
          allSerNodes.push(...serNodes);
        }

        // Parse category labels (from first series with cat reference)
        chartObj.categories = [];
        for (const ser of allSerNodes) {
          const catNode = qLocal(ser, "cat");
          if (!catNode) continue;
          const strCache = qLocal(catNode, "strCache") || qLocal(catNode, "numCache");
          if (strCache) {
            const catPts = qLocalAll(strCache, "pt");
            chartObj.categories = catPts.map(pt => {
              const v = qLocal(pt, "v");
              return v ? v.textContent : "";
            });
            if (chartObj.categories.length > 0) break;
          }
        }

        chartObj.placeholder = (chartObj.series.length === 0);
        console.log('[extractChartsFromZip] Parsed chart:', chartObj.id, 'type:', chartObj.chartType, 'series:', chartObj.series.length, 'title:', chartObj.title);
      }
    }
  } catch (err) {
    console.warn("Custom chart extraction failed", err);
  }
  return chartsBySheet;
}

// Parse OpenXML pivot table definitions to extract cell ranges and style info
async function extractPivotTablesFromZip(zip) {
  const pivotsBySheet = {};
  try {
    // Find all pivot table XML files
    const pivotFiles = Object.keys(zip.files).filter(f => f.match(/^xl\/pivotTables\/pivotTable\d+\.xml$/i));
    if (pivotFiles.length === 0) return pivotsBySheet;

    // Build a map: pivotTable path -> sheet name
    // We need to trace: sheet.xml.rels -> pivotTable reference
    const wbXml = await zip.file("xl/workbook.xml")?.async("string");
    if (!wbXml) return pivotsBySheet;
    const wbDoc = new DOMParser().parseFromString(wbXml, "application/xml");
    const sheetNodes = wbDoc.querySelectorAll("sheet");
    const sheetIdToName = {};
    sheetNodes.forEach(node => { sheetIdToName[node.getAttribute("r:id")] = node.getAttribute("name"); });

    const wbRelsXml = await zip.file("xl/_rels/workbook.xml.rels")?.async("string");
    if (!wbRelsXml) return pivotsBySheet;
    const wbRelsDoc = new DOMParser().parseFromString(wbRelsXml, "application/xml");
    const sheetPathToName = {};
    wbRelsDoc.querySelectorAll("Relationship").forEach(rel => {
      const target = rel.getAttribute("Target");
      const id = rel.getAttribute("Id");
      if (sheetIdToName[id]) sheetPathToName[target] = sheetIdToName[id];
    });

    // For each sheet, check its .rels for pivot table references
    for (const sheetPath of Object.keys(sheetPathToName)) {
      const sheetName = sheetPathToName[sheetPath];
      const parts = sheetPath.split("/");
      const filename = parts.pop();
      const sheetRelsPath = `xl/${parts.join("/")}/_rels/${filename}.rels`.replace(/\/\//g, "/");

      const sheetRelsFile = zip.file(sheetRelsPath);
      if (!sheetRelsFile) continue;

      const relsXml = await sheetRelsFile.async("string");
      const relsDoc = new DOMParser().parseFromString(relsXml, "application/xml");

      const pivotPaths = [];
      relsDoc.querySelectorAll("Relationship").forEach(rel => {
        if (rel.getAttribute("Type")?.includes("/pivotTable")) {
          let target = rel.getAttribute("Target");
          if (target.startsWith("../")) target = target.substring(3);
          pivotPaths.push(`xl/${target}`);
        }
      });

      if (pivotPaths.length === 0) continue;
      if (!pivotsBySheet[sheetName]) pivotsBySheet[sheetName] = [];

      for (const pivotPath of pivotPaths) {
        const pivotFile = zip.file(pivotPath);
        if (!pivotFile) continue;

        const pivotXml = await pivotFile.async("string");
        const pivotDoc = new DOMParser().parseFromString(pivotXml, "application/xml");

        // Get pivot table name
        const pivotRoot = pivotDoc.querySelector("pivotTableDefinition");
        const pivotName = pivotRoot?.getAttribute("name") || "PivotTable";

        // Get location ref (e.g. "A3:D15")
        const locationNode = pivotDoc.querySelector("location");
        const ref = locationNode?.getAttribute("ref");
        if (!ref) continue;

        // Parse the ref range (e.g. "A3:D15" -> {r1:2, c1:0, r2:14, c2:3})
        const rangeParts = ref.split(":");
        const parseCell = (cellRef) => {
          const match = cellRef.match(/^([A-Z]+)(\d+)$/);
          if (!match) return { r: 0, c: 0 };
          let col = 0;
          for (let i = 0; i < match[1].length; i++) {
            col = col * 26 + (match[1].charCodeAt(i) - 64);
          }
          return { r: parseInt(match[2]) - 1, c: col - 1 };
        };
        const tl = parseCell(rangeParts[0]);
        const br = rangeParts[1] ? parseCell(rangeParts[1]) : tl;

        // Get the number of header rows and data rows
        const firstHeaderRow = parseInt(locationNode?.getAttribute("firstHeaderRow") || "1");
        const firstDataRow = parseInt(locationNode?.getAttribute("firstDataRow") || "1");
        const firstDataCol = parseInt(locationNode?.getAttribute("firstDataCol") || "1");

        // Get style info
        const styleInfo = pivotDoc.querySelector("pivotTableStyleInfo");
        const styleName = styleInfo?.getAttribute("name") || "PivotStyleLight16";
        const showRowHeaders = styleInfo?.getAttribute("showRowHeaders") === "1";
        const showColHeaders = styleInfo?.getAttribute("showColHeaders") === "1";
        const showRowStripes = styleInfo?.getAttribute("showRowStripes") === "1";
        const showColStripes = styleInfo?.getAttribute("showColStripes") === "1";

        // Determine style colors based on style name
        let headerBg = "#4472C4";
        let headerColor = "#FFFFFF";
        let stripeBg = "#D6E4F0";
        let borderColor = "#B4C6E7";

        if (styleName.includes("Medium")) {
          if (styleName.includes("2") || styleName.includes("21")) { headerBg = "#ED7D31"; stripeBg = "#FCE4D6"; borderColor = "#F4B084"; }
          else if (styleName.includes("3") || styleName.includes("22")) { headerBg = "#A5A5A5"; stripeBg = "#EDEDED"; borderColor = "#C9C9C9"; }
          else if (styleName.includes("4") || styleName.includes("23")) { headerBg = "#FFC000"; stripeBg = "#FFF2CC"; borderColor = "#FFD966"; }
          else if (styleName.includes("5") || styleName.includes("24")) { headerBg = "#5B9BD5"; stripeBg = "#DDEBF7"; borderColor = "#9DC3E6"; }
          else if (styleName.includes("6") || styleName.includes("25")) { headerBg = "#70AD47"; stripeBg = "#E2EFDA"; borderColor = "#A9D18E"; }
        } else if (styleName.includes("Light")) {
          headerBg = "#FFFFFF"; headerColor = "#4472C4"; stripeBg = "#D6E4F0"; borderColor = "#B4C6E7";
          if (styleName.includes("2") || styleName.includes("14")) { headerColor = "#ED7D31"; stripeBg = "#FCE4D6"; borderColor = "#F4B084"; }
          else if (styleName.includes("3") || styleName.includes("15")) { headerColor = "#A5A5A5"; stripeBg = "#EDEDED"; borderColor = "#C9C9C9"; }
          else if (styleName.includes("4") || styleName.includes("16")) { headerColor = "#FFC000"; stripeBg = "#FFF2CC"; borderColor = "#FFD966"; }
          else if (styleName.includes("5") || styleName.includes("17")) { headerColor = "#5B9BD5"; stripeBg = "#DDEBF7"; borderColor = "#9DC3E6"; }
          else if (styleName.includes("6") || styleName.includes("18")) { headerColor = "#70AD47"; stripeBg = "#E2EFDA"; borderColor = "#A9D18E"; }
        } else if (styleName.includes("Dark")) {
          headerBg = "#4472C4"; headerColor = "#FFFFFF"; stripeBg = "#D6E4F0"; borderColor = "#4472C4";
        }

        pivotsBySheet[sheetName].push({
          name: pivotName,
          ref,
          range: { r1: tl.r, c1: tl.c, r2: br.r, c2: br.c },
          firstHeaderRow,
          firstDataRow,
          firstDataCol,
          styleName,
          showRowHeaders,
          showColHeaders,
          showRowStripes,
          showColStripes,
          colors: { headerBg, headerColor, stripeBg, borderColor },
        });
      }
    }
  } catch (err) {
    console.warn("Pivot table extraction failed", err);
  }
  return pivotsBySheet;
}

async function buildWorkbookModelsWithExcelJS(arrayBuffer) {
  const JSZip = (await import("jszip")).default;
  const ExcelJS = (await import("exceljs")).default;

  // Extract theme colors so theme-based fills resolve to actual hex.
  let themeColors = null;
  let zip = null;
  try {
    zip = await JSZip.loadAsync(arrayBuffer);
    const themeXmlFile =
      zip.file("xl/theme/theme1.xml") ||
      zip.file("xl/theme/theme.xml");
    if (themeXmlFile) {
      const themeXml = await themeXmlFile.async("string");
      const doc = new DOMParser().parseFromString(themeXml, "application/xml");
      const scheme = doc.querySelector("clrScheme");
      if (scheme) {
        const order = [
          "lt1",
          "dk1",
          "lt2",
          "dk2",
          "accent1",
          "accent2",
          "accent3",
          "accent4",
          "accent5",
          "accent6",
          "hlink",
          "folHlink",
        ];
        const colors = [];
        order.forEach((key) => {
          const node = scheme.querySelector(key);
          const srgb = node?.querySelector("srgbClr")?.getAttribute("val");
          const sys = node?.querySelector("sysClr")?.getAttribute("lastClr");
          const hex = (srgb || sys || "").trim();
          colors.push(/^[0-9a-fA-F]{6}$/.test(hex) ? hex.toUpperCase() : null);
        });
        themeColors = colors;
      }
    }
  } catch {
    themeColors = null;
  }

  const extractedZipCharts = await extractChartsFromZip(zip);
  const extractedPivotTables = await extractPivotTablesFromZip(zip);

  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(arrayBuffer);

  const dataMap = {};
  const sheetNames = [];

  wb.worksheets.forEach((sheet) => {
    const name = sheet.name || `Sheet${sheet.id}`;
    sheetNames.push(name);

    const maxDataRow = sheet.rowCount || 1;
    const maxDataCol = sheet.columnCount || 1;
    const targetCols = Math.max(maxDataCol + EXTRA_SCROLL_COLS, MIN_VISIBLE_COLS);
    const targetRows = Math.max(maxDataRow + EXTRA_SCROLL_ROWS, MIN_VISIBLE_ROWS);

    const rows = new Array(targetRows).fill(null).map(() => new Array(targetCols).fill(""));
    const formulas = {};
    const styles = {};
    const richHtml = {};
    const images = [];
    const charts = extractedZipCharts[sheet.name] || [];

    // Extract images from the worksheet (Single pass to prevent duplicates)
    try {
      const images1 = sheet.getImages() || [];
      images1.forEach((img) => {
        try {
          const imageId = img.imageId;
          const workbookImage = wb.getImage(imageId);
          if (workbookImage) {
            const base64Image = workbookImage.buffer.toString('base64');
            const mimeType = workbookImage.extension || 'png';
            const dataUrl = `data:image/${mimeType};base64,${base64Image}`;

            images.push({
              id: imageId,
              dataUrl,
              mimeType,
              range: img.range,
              editAs: img.editAs || 'oneCell',
              width: workbookImage.width || 100,
              height: workbookImage.height || 100,
            });
          }
        } catch (imgError) {
          console.warn('[SimpleExcelViewer] Failed to extract individual image:', imgError);
        }
      });
    } catch (imageError) {
      console.warn('[SimpleExcelViewer] Failed to extract images from sheet:', imageError);
    }

    // ExcelJS natively does NOT parse or support <c:chartSpace> graphics drawing references.
    // Charts deliberately omitted to avoid rendering generic, fake chart structures over the user grid.

    // Column widths (ExcelJS width is roughly "characters"; convert to px approximation)
    const colWidths = Array.from({ length: targetCols }, (_, idx) => {
      const col = sheet.getColumn(idx + 1);
      const w = typeof col.width === "number" ? col.width : undefined;
      if (!w) return DEFAULT_COL_WIDTH;
      return Math.max(MIN_COL_WIDTH, Math.round(w * 7 + 12));
    });

    // Row heights (points). Convert to px.
    const rowHeights = Array.from({ length: targetRows }, (_, idx) => {
      const row = sheet.getRow(idx + 1);
      const hpt = typeof row.height === "number" ? row.height : undefined;
      if (!hpt) return DEFAULT_ROW_HEIGHT;
      return Math.max(MIN_ROW_HEIGHT, Math.round(hpt * (96 / 72)));
    });

    // includeEmpty:true so we don't miss fills on cells that may have style but no value
    sheet.eachRow({ includeEmpty: true }, (row, rowNumber) => {
      if (rowNumber < 1 || rowNumber > targetRows) return;
      row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
        if (colNumber < 1 || colNumber > targetCols) return;

        const r = rowNumber - 1;
        const c = colNumber - 1;

        const v = cell.value;
        if (v && typeof v === "object" && "formula" in v) {
          formulas[`${r}:${c}`] = `=${v.formula || ""}`;
          rows[r][c] = cell.text ?? "";
        } else {
          rows[r][c] = cell.text ?? "";
        }

        // Rich text (per-run styling) - keep plain text in rows for filters,
        // but render rich HTML when present.
        if (v && typeof v === "object" && Array.isArray(v.richText)) {
          const html = richTextToHtml(v.richText, themeColors);
          if (html) richHtml[`${r}:${c}`] = html;
        }

        const css = {};
        const font = cell.font || {};
        const fill = cell.fill || {};

        const fontColor = resolveExcelColorToCss(font.color, themeColors);
        if (fontColor) css.color = fontColor;
        if (font.bold) css.fontWeight = 700;
        if (font.italic) css.fontStyle = "italic";
        if (font.underline) css.textDecoration = "underline";
        if (typeof font.name === "string") css.fontFamily = font.name;
        if (typeof font.size === "number") css.fontSize = `${Math.max(8, Math.round(font.size))}px`;

        const fg = resolveExcelColorToCss(fill.fgColor, themeColors);
        const bg = resolveExcelColorToCss(fill.bgColor, themeColors);

        // Apply any resolved fill color, regardless of pattern type.
        // This is more permissive and better matches how Excel visually shows fills.
        if (fg || bg) {
          css.backgroundColor = fg || bg;
        }

        if (Object.keys(css).length > 0) styles[`${r}:${c}`] = css;
      });
    });

    // Apply pivot table styling to cells that don't already have explicit backgrounds
    const pivotTables = extractedPivotTables[name] || [];
    pivotTables.forEach(pt => {
      const { range, firstDataRow, colors, showRowStripes } = pt;
      for (let r = range.r1; r <= range.r2; r++) {
        for (let c = range.c1; c <= range.c2; c++) {
          const key = `${r}:${c}`;
          const existing = styles[key] || {};

          // Header row (first row of pivot table)
          if (r === range.r1) {
            if (!existing.backgroundColor) {
              existing.backgroundColor = colors.headerBg;
            }
            if (!existing.color) {
              existing.color = colors.headerColor;
            }
            existing.fontWeight = existing.fontWeight || 700;
          }
          // Banded data rows
          else if (showRowStripes && ((r - range.r1) % 2 === 0)) {
            if (!existing.backgroundColor) {
              existing.backgroundColor = colors.stripeBg;
            }
          }

          // Border for all pivot cells
          if (!existing.borderBottom) {
            existing.borderBottom = `1px solid ${colors.borderColor}`;
          }
          if (!existing.borderRight) {
            existing.borderRight = `1px solid ${colors.borderColor}`;
          }

          // Row header column (first data column area) - bold
          if (c === range.c1 && r > range.r1) {
            existing.fontWeight = existing.fontWeight || 600;
          }

          styles[key] = existing;
        }
      }
    });

    dataMap[name] = { rows, maxCols: targetCols, colWidths, rowHeights, formulas, styles, richHtml, images, charts, pivotTables };
  });

  return { sheetNames, dataMap };
}

function FilterMenu({ colIndex, rows, currentFilter, onApply, onClose }) {
  const [searchTerm, setSearchTerm] = useState("");

  const allValues = useMemo(() => {
    const vals = new Set();
    rows.forEach(row => {
      const v = row[colIndex];
      vals.add(v == null || v === "" ? "(Blank)" : String(v));
    });
    return Array.from(vals).sort();
  }, [colIndex, rows]);

  const [selected, setSelected] = useState(() => {
    if (currentFilter) return new Set(currentFilter);
    return new Set(allValues);
  });

  const filteredValues = useMemo(() => {
    if (!searchTerm) return allValues;
    const lower = searchTerm.toLowerCase();
    return allValues.filter(v => v.toLowerCase().includes(lower));
  }, [allValues, searchTerm]);

  const isAllSelected = selected.size === allValues.length;
  const isAllFilteredSelected = filteredValues.every(v => selected.has(v));

  const toggleAll = () => {
    if (isAllFilteredSelected) {
      const next = new Set(selected);
      filteredValues.forEach(v => next.delete(v));
      setSelected(next);
    } else {
      const next = new Set(selected);
      filteredValues.forEach(v => next.add(v));
      setSelected(next);
    }
  };

  const toggleValue = (val) => {
    const next = new Set(selected);
    if (next.has(val)) next.delete(val);
    else next.add(val);
    setSelected(next);
  };

  return (
    <div className="filter-popup" onClick={e => { e.stopPropagation(); e.nativeEvent.stopImmediatePropagation(); }}>
      <div className="filter-search-container">
        <input
          type="text"
          placeholder="Search..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          className="filter-search-input"
          autoFocus
        />
      </div>
      <div className="filter-popup-list">
        {filteredValues.length > 0 && (
          <label className="filter-popup-item select-all-item">
            <input
              type="checkbox"
              checked={isAllFilteredSelected}
              onChange={toggleAll}
            />
            <span>(Select All Search Results)</span>
          </label>
        )}
        {filteredValues.map(val => (
          <label key={val} className="filter-popup-item">
            <input type="checkbox" checked={selected.has(val)} onChange={() => toggleValue(val)} />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{val}</span>
          </label>
        ))}
        {filteredValues.length === 0 && (
          <div style={{ padding: '8px', color: '#666', fontSize: '12px' }}>No matches found</div>
        )}
      </div>
      <div className="filter-popup-footer">
        <button type="button" className="filter-btn-ok" onClick={() => { onApply(selected.size === allValues.length ? null : selected); onClose(); }}>OK</button>
        <button type="button" className="filter-btn-cancel" onClick={onClose}>Cancel</button>
      </div>
    </div>
  );
}

function ExcelImage({ image, colWidths, rowHeights, zoomScale }) {
  const [imageSize, setImageSize] = useState({ width: image.width, height: image.height });
  const [position, setPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (!image.range) return;

    const range = image.range;
    const headerRowHeight = DEFAULT_ROW_HEIGHT;
    const headerColWidth = HEADER_COL_WIDTH; // 44px - matches CSS

    // Calculate top-left position
    let tlLeft = 0, tlTop = 0;
    for (let i = 0; i < (range.tl?.col || 0); i++) {
      tlLeft += (colWidths[i] || DEFAULT_COL_WIDTH);
    }
    for (let i = 0; i < (range.tl?.row || 0); i++) {
      tlTop += (rowHeights[i] || DEFAULT_ROW_HEIGHT);
    }

    // Add header offsets
    tlLeft += headerColWidth;
    tlTop += headerRowHeight;

    // Add EMU sub-cell offsets
    if (range.tl?.colOff) tlLeft += range.tl.colOff / 914400 * 96;
    if (range.tl?.rowOff) tlTop += range.tl.rowOff / 914400 * 96;

    let width = image.width;
    let height = image.height;

    // Calculate dimensions from bottom-right anchor
    if (range.br) {
      let brLeft = 0, brTop = 0;
      for (let i = 0; i < (range.br.col || 0); i++) {
        brLeft += (colWidths[i] || DEFAULT_COL_WIDTH);
      }
      for (let i = 0; i < (range.br.row || 0); i++) {
        brTop += (rowHeights[i] || DEFAULT_ROW_HEIGHT);
      }
      brLeft += headerColWidth;
      brTop += headerRowHeight;

      if (range.br.colOff) brLeft += range.br.colOff / 914400 * 96;
      if (range.br.rowOff) brTop += range.br.rowOff / 914400 * 96;

      width = Math.max(20, brLeft - tlLeft);
      height = Math.max(20, brTop - tlTop);
    }

    setPosition({ top: tlTop, left: tlLeft });
    setImageSize({ width, height });
  }, [image, colWidths, rowHeights]);

  const scaledWidth = imageSize.width * zoomScale;
  const scaledHeight = imageSize.height * zoomScale;

  return (
    <div
      className="excel-image-overlay"
      style={{
        position: 'absolute',
        top: position.top,
        left: position.left,
        width: scaledWidth,
        height: scaledHeight,
        pointerEvents: 'auto',
        zIndex: 10,
        transform: 'translate(0, 0)',
      }}
    >
      <img
        src={image.dataUrl}
        alt=""
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          maxWidth: 'none',
          maxHeight: 'none',
        }}
        draggable={false}
      />
    </div>
  );
}

function renderSimpleChart(canvas, chartObj) {
  const ctx = canvas.getContext('2d');
  const width = canvas.width;
  const height = canvas.height;
  const padding = { top: 52, right: 20, bottom: 36, left: 52 };
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;
  const chartType = chartObj.chartType || 'bar';

  ctx.clearRect(0, 0, width, height);
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, width, height);

  // Title
  ctx.fillStyle = '#333333';
  ctx.font = 'bold 13px Calibri, Arial';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  ctx.fillText(chartObj.title || 'Chart', width / 2, 8);

  // If combo chart, draw both bars and lines
  if (chartObj.isCombo) {
    drawComboChart(ctx, padding.left, padding.top, chartWidth, chartHeight, chartObj);
  } else {
    switch (chartType) {
      case 'bar':
      case 'col':
        drawBarChart(ctx, padding.left, padding.top, chartWidth, chartHeight, chartObj);
        break;
      case 'line':
        drawLineChart(ctx, padding.left, padding.top, chartWidth, chartHeight, chartObj);
        break;
      case 'pie':
      case 'doughnut':
        drawPieChart(ctx, width / 2, padding.top + chartHeight / 2, Math.min(chartWidth, chartHeight) / 2 - 10, chartObj);
        break;
      case 'area':
        drawLineChart(ctx, padding.left, padding.top, chartWidth, chartHeight, chartObj, true);
        break;
      case 'scatter':
        drawLineChart(ctx, padding.left, padding.top, chartWidth, chartHeight, chartObj);
        break;
      default:
        drawBarChart(ctx, padding.left, padding.top, chartWidth, chartHeight, chartObj);
    }
  }

  // Border
  ctx.strokeStyle = '#d0d0d0';
  ctx.lineWidth = 1;
  ctx.strokeRect(0.5, 0.5, width - 1, height - 1);
}

function drawBarChart(ctx, x, y, width, height, chartObj) {
  const seriesArr = chartObj?.series || [];
  if (seriesArr.length === 0) return;
  const numSeries = seriesArr.length;
  const numCategories = Math.max(...seriesArr.map(s => s.data.length));
  if (numCategories === 0) return;

  // Find global max/min for axis scaling
  let allVals = seriesArr.flatMap(s => s.data);
  const maxValue = Math.max(...allVals, 0) || 100;
  const minValue = Math.min(...allVals, 0);
  const range = maxValue - minValue || 100;

  const colors = ['#4472C4', '#ED7D31', '#A5A5A5', '#FFC000', '#5B9BD5', '#70AD47', '#264478', '#9B57A0'];
  const groupWidth = width / numCategories;
  const barWidth = (groupWidth * 0.8) / numSeries;
  const groupPad = groupWidth * 0.1;

  // Draw gridlines
  ctx.strokeStyle = '#E0E0E0';
  ctx.lineWidth = 1;
  const numGridLines = 5;
  for (let i = 0; i <= numGridLines; i++) {
    const gy = y + height - (i / numGridLines) * height;
    ctx.beginPath();
    ctx.moveTo(x, gy);
    ctx.lineTo(x + width, gy);
    ctx.stroke();
    // Axis labels
    const val = minValue + (i / numGridLines) * range;
    ctx.fillStyle = '#888';
    ctx.font = '9px Arial';
    ctx.textAlign = 'right';
    ctx.fillText(val % 1 === 0 ? val.toString() : val.toFixed(1), x - 4, gy + 3);
  }

  seriesArr.forEach((series, sIdx) => {
    series.data.forEach((value, cIdx) => {
      const barHeight = ((value - minValue) / range) * height;
      const barX = x + cIdx * groupWidth + groupPad + sIdx * barWidth;
      const barY = y + height - barHeight;

      ctx.fillStyle = colors[sIdx % colors.length];
      ctx.fillRect(barX, barY, barWidth, barHeight);
    });
  });

  // Category labels
  const cats = chartObj.categories || [];
  for (let i = 0; i < numCategories; i++) {
    ctx.fillStyle = '#666';
    ctx.font = '9px Arial';
    ctx.textAlign = 'center';
    ctx.fillText(cats[i] || `${i + 1}`, x + i * groupWidth + groupWidth / 2, y + height + 14);
  }

  // Axes
  ctx.strokeStyle = '#999';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(x, y + height);
  ctx.lineTo(x + width, y + height);
  ctx.moveTo(x, y);
  ctx.lineTo(x, y + height);
  ctx.stroke();

  // Legend
  if (numSeries >= 1) {
    ctx.font = '9px Calibri, Arial';
    ctx.textBaseline = 'middle';
    let totalW = 0;
    seriesArr.forEach(s => { totalW += 14 + ctx.measureText(s.name).width + 16; });
    let legendX = x + (width - totalW) / 2;
    const legendY = y + height + 22;
    seriesArr.forEach((s, i) => {
      ctx.fillStyle = colors[i % colors.length];
      ctx.fillRect(legendX, legendY - 5, 10, 10);
      ctx.fillStyle = '#333';
      ctx.textAlign = 'left';
      ctx.fillText(s.name, legendX + 14, legendY);
      legendX += 14 + ctx.measureText(s.name).width + 16;
    });
  }
}

function drawComboChart(ctx, x, y, width, height, chartObj) {
  const seriesArr = chartObj?.series || [];
  if (seriesArr.length === 0) return;
  const colors = ['#4472C4', '#ED7D31', '#A5A5A5', '#FFC000', '#5B9BD5', '#70AD47', '#264478', '#9B57A0'];

  const barSeries = seriesArr.filter(s => s.renderType === 'bar' || s.renderType === 'col');
  const lineSeries = seriesArr.filter(s => s.renderType !== 'bar' && s.renderType !== 'col');
  const numCategories = Math.max(...seriesArr.map(s => s.data.length), 1);

  // Compute global value range across ALL series
  const allVals = seriesArr.flatMap(s => s.data);
  const maxValue = Math.max(...allVals, 0) || 100;
  const minValue = Math.min(...allVals, 0);
  const range = maxValue - minValue || 100;

  // Nice axis ticks
  const numGridLines = 5;
  const step = range / numGridLines;
  const niceStep = Math.ceil(step * 2) / 2; // round to 0.5

  // Gridlines
  ctx.strokeStyle = '#E0E0E0';
  ctx.lineWidth = 1;
  for (let i = 0; i <= numGridLines; i++) {
    const gy = y + height - (i / numGridLines) * height;
    ctx.beginPath();
    ctx.moveTo(x, gy);
    ctx.lineTo(x + width, gy);
    ctx.stroke();
    const val = minValue + (i / numGridLines) * range;
    ctx.fillStyle = '#888';
    ctx.font = '9px Calibri, Arial';
    ctx.textAlign = 'right';
    ctx.textBaseline = 'middle';
    ctx.fillText(val % 1 === 0 ? val.toString() : val.toFixed(1), x - 6, gy);
  }

  const groupWidth = width / numCategories;

  // Draw bar series
  if (barSeries.length > 0) {
    const numBarSeries = barSeries.length;
    const barWidth = (groupWidth * 0.8) / numBarSeries;
    const groupPad = groupWidth * 0.1;

    barSeries.forEach((series, sIdx) => {
      const globalIdx = seriesArr.indexOf(series);
      series.data.forEach((value, cIdx) => {
        const barHeight = ((value - minValue) / range) * height;
        const barX = x + cIdx * groupWidth + groupPad + sIdx * barWidth;
        const barY = y + height - barHeight;

        ctx.fillStyle = colors[globalIdx % colors.length];
        ctx.fillRect(barX, barY, barWidth, barHeight);
      });
    });
  }

  // Draw line series
  lineSeries.forEach(series => {
    const globalIdx = seriesArr.indexOf(series);
    const data = series.data;
    if (!data || data.length === 0) return;
    const color = colors[globalIdx % colors.length];

    // Center points on each category's group
    const pointSpacing = groupWidth;

    // Line
    ctx.strokeStyle = color;
    ctx.lineWidth = 2.5;
    ctx.beginPath();
    data.forEach((value, index) => {
      const px = x + index * pointSpacing + pointSpacing / 2;
      const py = y + height - ((value - minValue) / range) * height;
      if (index === 0) ctx.moveTo(px, py);
      else ctx.lineTo(px, py);
    });
    ctx.stroke();

    // Points
    data.forEach((value, index) => {
      const px = x + index * pointSpacing + pointSpacing / 2;
      const py = y + height - ((value - minValue) / range) * height;
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(px, py, 3.5, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 1;
      ctx.stroke();
    });
  });

  // Category labels
  const cats = chartObj.categories || [];
  for (let i = 0; i < numCategories; i++) {
    ctx.fillStyle = '#666';
    ctx.font = '9px Calibri, Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.fillText(cats[i] || `${i + 1}`, x + i * groupWidth + groupWidth / 2, y + height + 6);
  }

  // Axes
  ctx.strokeStyle = '#999';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(x, y + height);
  ctx.lineTo(x + width, y + height);
  ctx.moveTo(x, y);
  ctx.lineTo(x, y + height);
  ctx.stroke();

  // Legend at bottom
  ctx.font = '9px Calibri, Arial';
  ctx.textBaseline = 'middle';
  // Calculate total legend width to center it
  let totalLegendW = 0;
  seriesArr.forEach(s => {
    totalLegendW += 14 + ctx.measureText(s.name).width + 16;
  });
  let legendX = x + (width - totalLegendW) / 2;
  const legendY = y + height + 22;

  seriesArr.forEach((s, i) => {
    const color = colors[i % colors.length];
    const isLine = s.renderType !== 'bar' && s.renderType !== 'col';

    if (isLine) {
      // Draw line indicator
      ctx.strokeStyle = color;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(legendX, legendY);
      ctx.lineTo(legendX + 10, legendY);
      ctx.stroke();
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(legendX + 5, legendY, 2.5, 0, Math.PI * 2);
      ctx.fill();
    } else {
      // Draw square indicator
      ctx.fillStyle = color;
      ctx.fillRect(legendX, legendY - 5, 10, 10);
    }

    ctx.fillStyle = '#333';
    ctx.textAlign = 'left';
    ctx.fillText(s.name, legendX + 14, legendY);
    legendX += 14 + ctx.measureText(s.name).width + 16;
  });
}

function drawLineChart(ctx, x, y, width, height, chartObj, fillArea) {
  const seriesArr = chartObj?.series || [];
  if (seriesArr.length === 0) return;

  let allVals = seriesArr.flatMap(s => s.data);
  const maxValue = Math.max(...allVals, 0) || 100;
  const minValue = Math.min(...allVals, 0);
  const range = maxValue - minValue || 100;
  const colors = ['#4472C4', '#ED7D31', '#A5A5A5', '#FFC000', '#5B9BD5', '#70AD47'];

  // Gridlines
  ctx.strokeStyle = '#E0E0E0';
  ctx.lineWidth = 1;
  const numGridLines = 5;
  for (let i = 0; i <= numGridLines; i++) {
    const gy = y + height - (i / numGridLines) * height;
    ctx.beginPath();
    ctx.moveTo(x, gy);
    ctx.lineTo(x + width, gy);
    ctx.stroke();
    const val = minValue + (i / numGridLines) * range;
    ctx.fillStyle = '#888';
    ctx.font = '9px Arial';
    ctx.textAlign = 'right';
    ctx.fillText(val % 1 === 0 ? val.toString() : val.toFixed(1), x - 4, gy + 3);
  }

  seriesArr.forEach((series, sIdx) => {
    const data = series.data;
    if (!data || data.length === 0) return;
    const pointSpacing = width / Math.max(1, data.length - 1);
    const color = colors[sIdx % colors.length];

    // Area fill
    if (fillArea) {
      ctx.globalAlpha = 0.2;
      ctx.fillStyle = color;
      ctx.beginPath();
      data.forEach((value, index) => {
        const px = x + index * pointSpacing;
        const py = y + height - ((value - minValue) / range) * height;
        if (index === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      });
      ctx.lineTo(x + (data.length - 1) * pointSpacing, y + height);
      ctx.lineTo(x, y + height);
      ctx.closePath();
      ctx.fill();
      ctx.globalAlpha = 1;
    }

    // Line
    ctx.strokeStyle = color;
    ctx.lineWidth = 2;
    ctx.beginPath();
    data.forEach((value, index) => {
      const px = x + index * pointSpacing;
      const py = y + height - ((value - minValue) / range) * height;
      if (index === 0) ctx.moveTo(px, py);
      else ctx.lineTo(px, py);
    });
    ctx.stroke();

    // Points
    data.forEach((value, index) => {
      const px = x + index * pointSpacing;
      const py = y + height - ((value - minValue) / range) * height;
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(px, py, 3, 0, Math.PI * 2);
      ctx.fill();
    });
  });

  // Category labels
  const cats = chartObj.categories || [];
  const numCategories = Math.max(...seriesArr.map(s => s.data.length));
  const catSpacing = width / Math.max(1, numCategories - 1);
  for (let i = 0; i < numCategories; i++) {
    ctx.fillStyle = '#666';
    ctx.font = '9px Arial';
    ctx.textAlign = 'center';
    ctx.fillText(cats[i] || `${i + 1}`, x + i * catSpacing, y + height + 14);
  }

  // Axes
  ctx.strokeStyle = '#999';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(x, y + height);
  ctx.lineTo(x + width, y + height);
  ctx.moveTo(x, y);
  ctx.lineTo(x, y + height);
  ctx.stroke();

  // Legend
  if (seriesArr.length >= 1) {
    ctx.font = '9px Calibri, Arial';
    ctx.textBaseline = 'middle';
    let totalW = 0;
    seriesArr.forEach(s => { totalW += 14 + ctx.measureText(s.name).width + 16; });
    let legendX = x + (width - totalW) / 2;
    const legendY = y + height + 22;
    seriesArr.forEach((s, i) => {
      const color = colors[i % colors.length];
      // Line indicator
      ctx.strokeStyle = color;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(legendX, legendY);
      ctx.lineTo(legendX + 10, legendY);
      ctx.stroke();
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(legendX + 5, legendY, 2.5, 0, Math.PI * 2);
      ctx.fill();

      ctx.fillStyle = '#333';
      ctx.textAlign = 'left';
      ctx.fillText(s.name, legendX + 14, legendY);
      legendX += 14 + ctx.measureText(s.name).width + 16;
    });
  }
}

function drawPieChart(ctx, centerX, centerY, radius, chartObj) {
  const data = chartObj?.series?.[0]?.data;
  if (!data || data.length === 0) return;
  const colors = ['#4472C4', '#ED7D31', '#A5A5A5', '#FFC000', '#5B9BD5', '#70AD47', '#264478', '#9B57A0'];
  const cats = chartObj.categories || [];
  const total = data.reduce((sum, value) => sum + Math.abs(value), 0);
  if (total === 0) return;
  let currentAngle = -Math.PI / 2;
  const isDoughnut = chartObj.chartType === 'doughnut';

  data.forEach((value, index) => {
    const sliceAngle = (Math.abs(value) / total) * Math.PI * 2;

    ctx.fillStyle = colors[index % colors.length];
    ctx.beginPath();
    ctx.moveTo(centerX, centerY);
    ctx.arc(centerX, centerY, radius, currentAngle, currentAngle + sliceAngle);
    ctx.closePath();
    ctx.fill();

    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 2;
    ctx.stroke();

    // Label
    const midAngle = currentAngle + sliceAngle / 2;
    const labelR = radius * 0.65;
    const lx = centerX + Math.cos(midAngle) * labelR;
    const ly = centerY + Math.sin(midAngle) * labelR;
    const pct = ((Math.abs(value) / total) * 100).toFixed(0);
    if (sliceAngle > 0.15) {
      ctx.fillStyle = '#fff';
      ctx.font = 'bold 10px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(`${pct}%`, lx, ly);
    }

    currentAngle += sliceAngle;
  });

  // Doughnut hole
  if (isDoughnut) {
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.arc(centerX, centerY, radius * 0.5, 0, Math.PI * 2);
    ctx.fill();
  }

  // Legend below
  const legendY = centerY + radius + 16;
  let legendX = centerX - (data.length * 40) / 2;
  data.forEach((value, i) => {
    ctx.fillStyle = colors[i % colors.length];
    ctx.fillRect(legendX, legendY, 8, 8);
    ctx.fillStyle = '#333';
    ctx.font = '9px Arial';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    const label = cats[i] || `${i + 1}`;
    ctx.fillText(label, legendX + 12, legendY);
    legendX += ctx.measureText(label).width + 24;
  });
}

const HEADER_COL_WIDTH = 44; // Must match CSS .corner-cell / tbody th width

function ExcelChart({ chart, colWidths, rowHeights, zoomScale }) {
  const canvasRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [size, setSize] = useState({ width: 400, height: 300 });

  useEffect(() => {
    if (!chart.range) return;

    const range = chart.range;
    const headerRowHeight = DEFAULT_ROW_HEIGHT; // Height of column header row (A, B, C...)
    const headerColWidth = HEADER_COL_WIDTH;     // Width of row number column

    // Calculate top-left position (sum of row/col sizes up to tl)
    let tlLeft = 0, tlTop = 0;
    for (let i = 0; i < (range.tl?.col || 0); i++) {
      tlLeft += (colWidths[i] || DEFAULT_COL_WIDTH);
    }
    for (let i = 0; i < (range.tl?.row || 0); i++) {
      tlTop += (rowHeights[i] || DEFAULT_ROW_HEIGHT);
    }
    // Add EMU sub-cell offsets
    if (range.tl?.colOff) tlLeft += range.tl.colOff / 914400 * 96;
    if (range.tl?.rowOff) tlTop += range.tl.rowOff / 914400 * 96;

    // Add header offsets
    tlLeft += headerColWidth;
    tlTop += headerRowHeight;

    // Calculate bottom-right position
    let brLeft = 0, brTop = 0;
    if (range.br) {
      for (let i = 0; i < range.br.col; i++) {
        brLeft += (colWidths[i] || DEFAULT_COL_WIDTH);
      }
      for (let i = 0; i < range.br.row; i++) {
        brTop += (rowHeights[i] || DEFAULT_ROW_HEIGHT);
      }
      if (range.br.colOff) brLeft += range.br.colOff / 914400 * 96;
      if (range.br.rowOff) brTop += range.br.rowOff / 914400 * 96;

      brLeft += headerColWidth;
      brTop += headerRowHeight;
    }

    const width = range.br ? Math.max(200, brLeft - tlLeft) : 400;
    const height = range.br ? Math.max(150, brTop - tlTop) : 300;

    console.log('[ExcelChart] Calculated position:', {
      chartId: chart.id,
      chartType: chart.chartType,
      seriesCount: chart.series?.length,
      range,
      top: tlTop,
      left: tlLeft,
      width,
      height
    });

    setPosition({ top: tlTop, left: tlLeft });
    setSize({ width, height });
  }, [chart, colWidths, rowHeights]);

  const scaledWidth = Math.round(size.width * zoomScale);
  const scaledHeight = Math.round(size.height * zoomScale);

  // Render chart on canvas when component mounts or size changes
  useEffect(() => {
    if (canvasRef.current && !chart.placeholder) {
      renderSimpleChart(canvasRef.current, chart);
    }
  }, [scaledWidth, scaledHeight, chart]);

  if (chart.placeholder) {
    return (
      <div
        className="excel-chart-placeholder"
        style={{
          position: 'absolute',
          top: position.top,
          left: position.left,
          width: scaledWidth,
          height: scaledHeight,
          border: '2px dashed #ccc',
          backgroundColor: '#f9f9f9',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10,
          pointerEvents: 'auto',
        }}
      >
        <div style={{ textAlign: 'center', color: '#666' }}>
          <div style={{ fontSize: '14px', fontWeight: 'bold' }}>📊 Chart</div>
          <div style={{ fontSize: '12px' }}>{chart.title || 'Chart'}</div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="excel-chart-overlay"
      style={{
        position: 'absolute',
        top: position.top,
        left: position.left,
        width: scaledWidth,
        height: scaledHeight,
        zIndex: 10,
        pointerEvents: 'auto',
      }}
    >
      <canvas
        ref={canvasRef}
        width={scaledWidth}
        height={scaledHeight}
        style={{
          width: '100%',
          height: '100%',
          border: '1px solid #ddd',
          backgroundColor: '#fff',
          borderRadius: '4px',
          boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
        }}
      />
    </div>
  );
}

const SimpleExcelViewer = forwardRef(({
  fileId,
  userId,
  configPath,
  height = "calc(87vh - 62px)",
  zoom = 1,
}, ref) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sheetNames, setSheetNames] = useState([]);
  const [activeSheet, setActiveSheet] = useState("");
  const [sheetDataByName, setSheetDataByName] = useState({});
  const [selectedCell, setSelectedCell] = useState({ row: 0, col: 0 });
  const [selection, setSelection] = useState({
    anchor: { row: 0, col: 0 },
    focus: { row: 0, col: 0 },
    dragging: false,
  });
  const [resizeState, setResizeState] = useState(null);
  const gridWrapperRef = useRef(null);
  const scrollBySheetRef = useRef({});

  const [filters, setFilters] = useState({});
  const [activeFilterCol, setActiveFilterCol] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const loadWorkbook = async () => {
      try {
        setLoading(true);
        setError("");

        const apiBase = process.env.REACT_APP_API || "";
        const cfgPath =
          configPath ||
          process.env.REACT_APP_ONLYOFFICE_CONFIG_PATH ||
          "onlyoffice/config";
        const normalizedPath = cfgPath.replace(/^\/+|\/+$/g, "");
        const configUrl = `${apiBase}/${normalizedPath}/${fileId}?userId=${encodeURIComponent(
          userId || "anonymous"
        )}`;

        const configResp = await fetch(configUrl, {
          method: "GET",
          credentials: "include",
        });
        if (!configResp.ok) {
          throw new Error("Unable to load Excel file details.");
        }

        const configJson = await configResp.json();
        const config = configJson?.data || configJson;
        const excelUrl = config?.document?.url;
        if (!excelUrl) {
          throw new Error("Excel source URL not found.");
        }

        const fileResp = await fetch(excelUrl, {
          method: "GET",
          credentials: "include",
        });
        if (!fileResp.ok) {
          throw new Error("Unable to fetch Excel file.");
        }

        const workbookBuffer = await fileResp.arrayBuffer();

        // Prefer ExcelJS for .xlsx to get reliable styles (font/fill).
        // Fallback to SheetJS for .xls or if ExcelJS parsing fails.
        let names = [];
        let dataMap = {};
        const lowerUrl = String(excelUrl || "").toLowerCase();
        const fileType = String(config?.document?.fileType || "").toLowerCase();

        // Always try ExcelJS unless we explicitly know it's a legacy .xls format
        const isLegacyXls = fileType === "xls" || (lowerUrl.includes(".xls") && !lowerUrl.includes(".xlsx"));

        if (!isLegacyXls) {
          try {
            const result = await buildWorkbookModelsWithExcelJS(workbookBuffer);
            names = result.sheetNames;
            dataMap = result.dataMap;
          } catch (e) {
            console.error("[SimpleExcelViewer] ExcelJS parsing failed, falling back to basic reader:", e);
            // fall back below
          }
        }

        if (names.length === 0) {
          const workbook = XLSX.read(workbookBuffer, {
            type: "array",
            cellFormula: true,
            cellStyles: true,
          });
          names = workbook.SheetNames || [];
          names.forEach((name) => {
            const ws = workbook.Sheets[name];
            dataMap[name] = buildSheetModel(ws || {});
          });
        }

        if (!cancelled) {
          setSheetNames(names);
          setSheetDataByName(dataMap);
          setActiveSheet((prev) => (prev && names.includes(prev) ? prev : names[0] || ""));
          setSelectedCell({ row: 0, col: 0 });
          setSelection({ anchor: { row: 0, col: 0 }, focus: { row: 0, col: 0 }, dragging: false });
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err?.message || "Failed to load Excel.");
          setLoading(false);
        }
      }
    };

    if (fileId) {
      loadWorkbook();
    } else {
      setLoading(false);
      setError("File details are missing.");
    }

    return () => {
      cancelled = true;
    };
  }, [configPath, fileId, userId]);

  // Imperative handle to trigger local printing for this component
  const componentRef = useRef(null);
  useImperativeHandle(ref, () => ({
    printAsPdf: () => {
      if (!componentRef.current) return;
      const iframe = document.createElement("iframe");
      iframe.style.display = "none";
      document.body.appendChild(iframe);

      const targetDoc = iframe.contentWindow.document;
      targetDoc.open();
      // Inject essential table and cell styles to guarantee the print matches the screen perfectly
      targetDoc.write(`
        <html>
          <head>
            <title>Print Excel</title>
            <style>
              body { font-family: Calibri, "Segoe UI", Arial, sans-serif; padding: 20px; }
              .simple-excel-grid { border-collapse: collapse; width: max-content; }
              .simple-excel-grid th, .simple-excel-grid td { 
                border: 1px solid #e3e3e3; padding: 4px 6px; font-size: 12px; white-space: nowrap; 
              }
              .simple-excel-grid thead th { background: #f3f3f3; font-weight: bold; text-align: center; }
              .simple-excel-grid tbody th { background: #f3f3f3; font-weight: bold; text-align: center; }
              @media print {
                @page { margin: 0.5cm; }
                * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
              }
            </style>
          </head>
          <body>
            \${componentRef.current.outerHTML}
          </body>
        </html>
      `);
      targetDoc.close();

      // Allow browser to render DOM off-screen before triggering print dialog
      setTimeout(() => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
        setTimeout(() => document.body.removeChild(iframe), 1000);
      }, 500);
    }
  }));

  const activeModel = useMemo(() => {
    if (!activeSheet) return null;
    return sheetDataByName[activeSheet] || null;
  }, [activeSheet, sheetDataByName]);

  const normalizedRows = useMemo(() => activeModel?.rows || [], [activeModel?.rows]);
  const maxCols = activeModel?.maxCols || MIN_VISIBLE_COLS;
  const colWidths = activeModel?.colWidths || new Array(maxCols).fill(DEFAULT_COL_WIDTH);
  const rowHeights = activeModel?.rowHeights || new Array(normalizedRows.length).fill(DEFAULT_ROW_HEIGHT);
  const cellStyles = activeModel?.styles || {};
  const cellRichHtml = activeModel?.richHtml || {};
  const sheetImages = useMemo(() => activeModel?.images || [], [activeModel?.images]);
  const sheetCharts = useMemo(() => activeModel?.charts || [], [activeModel?.charts]);

  // Debug: Log visual content data
  useEffect(() => {
    console.log('[SimpleExcelViewer] Sheet data updated:', {
      sheetName: activeSheet,
      imagesCount: sheetImages.length,
      chartsCount: sheetCharts.length,
      images: sheetImages,
      charts: sheetCharts,
      colWidths: colWidths.slice(0, 10), // First 10 columns
      rowHeights: rowHeights.slice(0, 10) // First 10 rows
    });
  }, [activeSheet, sheetImages, sheetCharts]);

  const normalizedSelection = useMemo(() => {
    const r1 = Math.min(selection.anchor.row, selection.focus.row);
    const r2 = Math.max(selection.anchor.row, selection.focus.row);
    const c1 = Math.min(selection.anchor.col, selection.focus.col);
    const c2 = Math.max(selection.anchor.col, selection.focus.col);
    return { r1, r2, c1, c2 };
  }, [selection.anchor.col, selection.anchor.row, selection.focus.col, selection.focus.row]);

  const selectionSummary = useMemo(() => {
    const rowsCount = normalizedSelection.r2 - normalizedSelection.r1 + 1;
    const colsCount = normalizedSelection.c2 - normalizedSelection.c1 + 1;
    return rowsCount === 1 && colsCount === 1 ? "" : `${rowsCount}R x ${colsCount}C`;
  }, [normalizedSelection.c1, normalizedSelection.c2, normalizedSelection.r1, normalizedSelection.r2]);

  const selectedCellLabel = useMemo(
    () => `${toColumnLabel(selectedCell.col)}${selectedCell.row + 1}`,
    [selectedCell.col, selectedCell.row]
  );

  const selectedCellText = useMemo(() => {
    if (!activeModel) return "";
    const formula = activeModel.formulas?.[`${selectedCell.row}:${selectedCell.col}`];
    if (formula) return formula;
    const value = activeModel.rows?.[selectedCell.row]?.[selectedCell.col];
    return value == null ? "" : String(value);
  }, [activeModel, selectedCell.col, selectedCell.row]);

  // Clear filters on sheet change
  useEffect(() => {
    setFilters({});
    setActiveFilterCol(null);
  }, [activeSheet]);

  const toggleFilterMenu = (colIndex, event) => {
    event.stopPropagation();
    event.nativeEvent.stopImmediatePropagation();
    setActiveFilterCol(prev => prev === colIndex ? null : colIndex);
  };

  // Close filter menu clicking outside
  useEffect(() => {
    if (activeFilterCol === null) return undefined;
    const onClick = () => setActiveFilterCol(null);
    window.addEventListener("click", onClick);
    return () => window.removeEventListener("click", onClick);
  }, [activeFilterCol]);

  const applyFilter = (colIndex, selectedValues) => {
    setFilters(prev => {
      const next = { ...prev };
      if (!selectedValues) delete next[colIndex];
      else next[colIndex] = selectedValues;
      return next;
    });
  };

  const isRowVisible = useCallback((rowIndex) => {
    if (Object.keys(filters).length === 0) return true;
    for (const colIndex in filters) {
      const allowed = filters[colIndex];
      if (allowed && allowed.size > 0) {
        const v = normalizedRows[rowIndex][colIndex];
        const strVal = v == null || v === "" ? "(Blank)" : String(v);
        if (!allowed.has(strVal)) return false;
      }
    }
    return true;
  }, [filters, normalizedRows]);

  useEffect(() => {
    if (!selection.dragging) return undefined;
    const onMouseUp = () => setSelection((prev) => ({ ...prev, dragging: false }));
    window.addEventListener("mouseup", onMouseUp);
    return () => window.removeEventListener("mouseup", onMouseUp);
  }, [selection.dragging]);

  useEffect(() => {
    if (!resizeState) return undefined;

    const onMouseMove = (event) => {
      const delta = resizeState.type === "col" ? event.clientX - resizeState.start : event.clientY - resizeState.start;
      const nextSize = Math.max(
        resizeState.type === "col" ? MIN_COL_WIDTH : MIN_ROW_HEIGHT,
        resizeState.initial + delta
      );

      setSheetDataByName((prev) => {
        const sheet = prev[activeSheet];
        if (!sheet) return prev;

        if (resizeState.type === "col") {
          const nextCols = [...sheet.colWidths];
          nextCols[resizeState.index] = nextSize;
          return { ...prev, [activeSheet]: { ...sheet, colWidths: nextCols } };
        }

        const nextRows = [...sheet.rowHeights];
        nextRows[resizeState.index] = nextSize;
        return { ...prev, [activeSheet]: { ...sheet, rowHeights: nextRows } };
      });
    };

    const onMouseUp = () => {
      setResizeState(null);
    };

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);

    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, [activeSheet, resizeState]);

  // Keep scroll position independent per sheet.
  useEffect(() => {
    const el = gridWrapperRef.current;
    if (!el || !activeSheet) return undefined;

    const onScroll = () => {
      scrollBySheetRef.current[activeSheet] = { left: el.scrollLeft, top: el.scrollTop };
    };

    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [activeSheet]);

  useEffect(() => {
    const el = gridWrapperRef.current;
    if (!el || !activeSheet) return;
    const pos = scrollBySheetRef.current[activeSheet];
    if (!pos) return;
    requestAnimationFrame(() => {
      el.scrollLeft = pos.left || 0;
      el.scrollTop = pos.top || 0;
    });
  }, [activeSheet]);

  const startResize = (type, index, event) => {
    event.preventDefault();
    event.stopPropagation();
    setResizeState({
      type,
      index,
      start: type === "col" ? event.clientX : event.clientY,
      initial: type === "col" ? colWidths[index] : rowHeights[index],
    });
  };

  const startSelection = (row, col, event) => {
    const isShift = event.shiftKey;
    const isToggle = event.ctrlKey || event.metaKey;

    if (isShift) {
      setSelection((prev) => ({
        anchor: prev.anchor,
        focus: { row, col },
        dragging: true,
      }));
      setSelectedCell({ row, col });
      return;
    }

    if (isToggle) {
      setSelection({
        anchor: { row, col },
        focus: { row, col },
        dragging: false,
      });
      setSelectedCell({ row, col });
      return;
    }

    setSelection({
      anchor: { row, col },
      focus: { row, col },
      dragging: true,
    });
    setSelectedCell({ row, col });
  };

  const extendSelection = (row, col) => {
    setSelection((prev) => (prev.dragging ? { ...prev, focus: { row, col } } : prev));
  };

  if (loading) {
    return <div className="simple-excel-viewer-status">Loading Excel...</div>;
  }

  if (error) {
    return <div className="simple-excel-viewer-status simple-excel-viewer-error">{error}</div>;
  }

  if (sheetNames.length === 0) {
    return <div className="simple-excel-viewer-status">No sheets found in this file.</div>;
  }

  const zoomScale = zoom === 1 ? 1 : zoom === 2 ? 1.25 : zoom === 3 ? 1.5 : zoom === 4 ? 1.75 : 2;

  return (
    <div className="simple-excel-viewer" style={{ height }}>
      <div className="simple-excel-formula-bar">
        <div className="simple-excel-name-box">{selectedCellLabel}</div>
        <div className="simple-excel-formula-input" title={selectedCellText}>
          {selectionSummary ? `Selection: ${selectionSummary}` : selectedCellText}
        </div>
      </div>

      <div className="simple-excel-grid-wrapper" ref={gridWrapperRef} style={{ position: 'relative' }}>
        <table className="simple-excel-grid" style={{ zoom: zoomScale }} ref={componentRef}>
          <thead>
            <tr>
              <th className="corner-cell" />
              {Array.from({ length: maxCols }).map((_, colIndex) => (
                <th
                  key={`col-${colIndex}`}
                  style={{ minWidth: `${colWidths[colIndex]}px` }}
                >
                  {toColumnLabel(colIndex)}
                  <span
                    className={`filter-excel-btn ${filters[colIndex] ? 'filtered' : ''}`}
                    onClick={(e) => toggleFilterMenu(colIndex, e)}
                    title="Filter"
                  >
                    {filters[colIndex] ? (
                      <span className="material-icons" style={{ fontSize: '13px' }}>filter_alt</span>
                    ) : (
                      <span className="material-icons" style={{ fontSize: '15px' }}>arrow_drop_down</span>
                    )}
                  </span>
                  {activeFilterCol === colIndex && (
                    <FilterMenu
                      colIndex={colIndex}
                      rows={normalizedRows}
                      currentFilter={filters[colIndex]}
                      onApply={(sel) => applyFilter(colIndex, sel)}
                      onClose={() => setActiveFilterCol(null)}
                    />
                  )}
                  <span
                    className="col-resize-handle"
                    onMouseDown={(event) => startResize("col", colIndex, event)}
                  />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {normalizedRows.map((row, rowIndex) => {
              if (!isRowVisible(rowIndex)) return null;
              return (
                <tr key={`row-${rowIndex}`} style={{ minHeight: `${rowHeights[rowIndex]}px` }}>
                  <th style={{ minHeight: `${rowHeights[rowIndex]}px` }}>
                    {rowIndex + 1}
                    <span
                      className="row-resize-handle"
                      onMouseDown={(event) => startResize("row", rowIndex, event)}
                    />
                  </th>
                  {row.map((cell, colIndex) => (
                    (() => {
                      const key = `${rowIndex}:${colIndex}`;
                      const html = cellRichHtml[key] || textToHtml(cell ?? "");
                      return (
                        <td
                          key={`cell-${rowIndex}-${colIndex}`}
                          onMouseDown={(event) => startSelection(rowIndex, colIndex, event)}
                          onMouseEnter={() => extendSelection(rowIndex, colIndex)}
                          className={[
                            selectedCell.row === rowIndex && selectedCell.col === colIndex ? "selected-cell" : "",
                            rowIndex >= normalizedSelection.r1 &&
                              rowIndex <= normalizedSelection.r2 &&
                              colIndex >= normalizedSelection.c1 &&
                              colIndex <= normalizedSelection.c2
                              ? `in-selection ${rowIndex === normalizedSelection.r1 ? 'sel-top' : ''} ${rowIndex === normalizedSelection.r2 ? 'sel-bottom' : ''} ${colIndex === normalizedSelection.c1 ? 'sel-left' : ''} ${colIndex === normalizedSelection.c2 ? 'sel-right' : ''}`
                              : "",
                          ].join(" ").trim()}
                          style={{
                            minWidth: `${colWidths[colIndex]}px`,
                            maxWidth: `${colWidths[colIndex]}px`,
                            ...(cellStyles[`${rowIndex}:${colIndex}`] || {}),
                          }}
                          title={cell == null ? "" : String(cell)}
                        >
                          <div
                            className="simple-excel-cell"
                            dangerouslySetInnerHTML={{ __html: html }}
                          />
                        </td>
                      );
                    })()
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* Visual Content Overlay */}
        <div className="excel-visual-content-overlay" style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}>
          {sheetImages.map((image, index) => (
            <div key={`image-${index}`} style={{ pointerEvents: 'auto' }}>
              <ExcelImage
                image={image}
                colWidths={colWidths}
                rowHeights={rowHeights}
                zoomScale={zoomScale}
              />
            </div>
          ))}
          {sheetCharts.map((chart, index) => (
            <div key={`chart-${index}`} style={{ pointerEvents: 'auto' }}>
              <ExcelChart
                chart={chart}
                colWidths={colWidths}
                rowHeights={rowHeights}
                zoomScale={zoomScale}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="simple-excel-sheetbar">
        <div className="simple-excel-tabs" role="tablist" aria-label="Sheets">
          {sheetNames.map((name) => (
            <button
              key={name}
              type="button"
              className={`simple-excel-tab ${activeSheet === name ? "active" : ""}`}
              onClick={() => setActiveSheet(name)}
              title={name}
            >
              {name}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
});

export default memo(SimpleExcelViewer);
